import { selectFeatureFlag } from 'features/feature-set/featuresSlice';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { createRecordingBookmarkRequested } from 'features/recorder/actions';
import {
  selectRecorderStatus,
  selectRecordingBookmarksEnabled,
} from 'features/recorder/recorderSlice';
import { recordingBookmarkSchema } from 'features/recorder/schemas';
import { RecorderStatus } from 'features/recorder/types';
import { call, put, select } from 'redux-saga/effects';
import { eventBus } from 'utils/eventBus';
import { fromZodError } from 'zod-validation-error';

export function* onAddRecordingBookmarkCommandSaga(data: unknown) {
  const recordingsEnabled: boolean = yield select(selectFeatureFlag, 'recordings');
  if (!recordingsEnabled) {
    yield call(
      eventBus.notAllowedError,
      'Recording disabled. You’ll need to edit this room’s properties to record sessions in this room',
      'recording'
    );
    return;
  }

  const bookmarksEnabled: boolean = yield select(selectRecordingBookmarksEnabled);
  if (!bookmarksEnabled) {
    yield call(
      eventBus.notAllowedError,
      'Recording bookmarks disabled. You’ll need to edit this room’s properties to add bookmarks in this room',
      'recording'
    );
    return;
  }

  const canControlRecording: boolean = yield call(hasPermissionsSaga, PermissionTypes.recording);
  if (!canControlRecording) {
    yield call(
      eventBus.forbiddenActionError,
      'Forbidden action. This participant is not allowed to add recording bookmarks',
      'recording'
    );
    return;
  }

  const recorderStatus: RecorderStatus = yield select(selectRecorderStatus);
  if (recorderStatus !== 'started') {
    yield call(eventBus.error, {
      name: 'add-recording-bookmark',
      message: 'No active recording. Start a recording before adding a bookmark.',
    });
    return;
  }

  const validationResult = recordingBookmarkSchema.safeParse(data);
  if (!validationResult.success) {
    yield call(eventBus.error, {
      name: 'add-recording-bookmark',
      message: fromZodError(validationResult.error).toString(),
    });
    return;
  }

  yield put(createRecordingBookmarkRequested({ bookmark: validationResult.data.bookmark }));
}
